import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, CreditCard, Briefcase, Wallet, Plus, ArrowRight, ChevronLeft } from 'lucide-react';

// Map backend account_type → frontend ACCOUNT_TYPES enum.
// Backend emits: bank | credit_card | brokerage
// Frontend uses: bank | credit_card | wallet | cash | demat | investment | other
export function mapAccountTypeForFE(t) {
  if (t === 'brokerage') return 'demat';
  if (t === 'bank' || t === 'credit_card') return t;
  return 'bank';
}

const TYPE_ICON = {
  bank: Building2,
  credit_card: CreditCard,
  brokerage: Briefcase,
  demat: Briefcase,
};

const TYPE_LABEL = {
  bank: 'Bank account',
  credit_card: 'Credit card',
  brokerage: 'Brokerage / Demat',
  demat: 'Brokerage / Demat',
};

/**
 * StatementAccountDialog
 *
 * Shown after a statement PDF has been parsed and we detected enough account
 * metadata (last4 / bank_name / currency) to confidently propose linking the
 * resulting transactions to a specific account, but no existing account on
 * file matches. Always asks; never silently creates.
 *
 * Three actions:
 *   • Add this account     → calls onAddNew(prefill)
 *   • Link to existing     → calls onLinkExisting(accountId)
 *   • Decide later         → calls onSkip()
 *
 * Props:
 *   open: boolean
 *   onOpenChange(open: boolean)
 *   account: {
 *     bank_name, account_type, last4, currency,
 *     statement_period_start, statement_period_end,
 *     closing_balance, detection_confidence
 *   }
 *   accounts: array of existing accounts (for "Link to existing" picker)
 *   txnCount: number of transactions parsed (for context line)
 *   onAddNew(prefill): user accepted; create account with these defaults
 *   onLinkExisting(accountId): user picked an existing account
 *   onSkip(): user wants to decide later (proceed to preview)
 */
export default function StatementAccountDialog({
  open,
  onOpenChange,
  account,
  accounts = [],
  txnCount = 0,
  onAddNew,
  onLinkExisting,
  onSkip,
}) {
  const [mode, setMode] = useState('choose'); // 'choose' | 'pick-existing'
  const [pickedId, setPickedId] = useState('');

  // Reset internal state whenever the dialog opens
  React.useEffect(() => {
    if (open) {
      setMode('choose');
      setPickedId('');
    }
  }, [open]);

  const feType = useMemo(() => mapAccountTypeForFE(account?.account_type), [account]);
  const Icon = TYPE_ICON[account?.account_type] || TYPE_ICON[feType] || Wallet;
  const typeLabel = TYPE_LABEL[account?.account_type] || TYPE_LABEL[feType] || 'Account';

  // Suggested display name: "HDFC Bank Credit Card" or "ICICI Bank Savings"
  const suggestedName = useMemo(() => {
    if (!account) return '';
    const bn = (account.bank_name || '').trim();
    if (!bn) {
      return account.last4 ? `Account ••${account.last4}` : 'New account';
    }
    if (account.account_type === 'credit_card') return `${bn} Credit Card`;
    if (account.account_type === 'brokerage') return `${bn} Brokerage`;
    return bn;
  }, [account]);

  const handleAddNew = () => {
    onAddNew?.({
      name: suggestedName,
      type: feType,
      last4: account?.last4 || '',
      currency: account?.currency || null,
      // closing_balance comes back as a number, the Accounts form takes a string-ish
      balance:
        account?.closing_balance != null && Number.isFinite(account.closing_balance)
          ? String(account.closing_balance)
          : '',
    });
  };

  const handleLink = () => {
    if (!pickedId) return;
    onLinkExisting?.(pickedId);
  };

  if (!account) return null;

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-2xl sm:max-w-md"
        data-testid="statement-account-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            {mode === 'pick-existing' ? 'Link to an existing account' : 'New account detected'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'pick-existing'
              ? `Which account do these ${txnCount || ''} transactions belong to?`
              : 'This statement looks like it’s from an account that isn’t in 2120 yet. Want to add it now?'}
          </DialogDescription>
        </DialogHeader>

        {/* ---------- Choose mode ---------- */}
        {mode === 'choose' && (
          <>
            <div
              className="rounded-xl border border-border/70 bg-muted/40 p-4 space-y-2"
              data-testid="statement-account-summary"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate" data-testid="statement-account-name">
                    {suggestedName}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {typeLabel}
                    {account.currency ? ` · ${account.currency}` : ''}
                  </div>
                </div>
                {account.last4 && (
                  <Badge
                    variant="secondary"
                    className="rounded-full tabular-nums tracking-widest"
                    data-testid="statement-account-last4"
                  >
                    ••{account.last4}
                  </Badge>
                )}
              </div>

              {(account.statement_period_start || account.statement_period_end || txnCount > 0) && (
                <div className="text-[11px] text-muted-foreground border-t border-border/50 pt-2">
                  {txnCount > 0 && (
                    <>
                      <span className="text-foreground tabular-nums">{txnCount}</span>
                      {' transactions'}
                    </>
                  )}
                  {(account.statement_period_start || account.statement_period_end) && (
                    <>
                      {txnCount > 0 ? ' · ' : ''}
                      Statement period{' '}
                      <span className="text-foreground">
                        {account.statement_period_start || '?'} → {account.statement_period_end || '?'}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground">
              We only store the last 4 digits, never the full account or card number.
            </p>

            <DialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2 sm:items-stretch">
              <Button
                onClick={handleAddNew}
                className="rounded-xl w-full sm:w-full justify-center"
                data-testid="statement-account-add-new"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add this account
              </Button>
              <Button
                variant="outline"
                onClick={() => setMode('pick-existing')}
                disabled={(accounts || []).length === 0}
                className="rounded-xl w-full sm:w-full justify-center"
                data-testid="statement-account-link-existing"
              >
                Link to existing account
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => onSkip?.()}
                className="rounded-xl w-full sm:w-full justify-center text-muted-foreground hover:text-foreground"
                data-testid="statement-account-skip"
              >
                Decide later
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ---------- Pick-existing mode ---------- */}
        {mode === 'pick-existing' && (
          <>
            <div className="space-y-2">
              <Select value={pickedId || undefined} onValueChange={setPickedId}>
                <SelectTrigger
                  className="rounded-xl"
                  data-testid="statement-account-existing-select"
                >
                  <SelectValue placeholder="Choose an account…" />
                </SelectTrigger>
                <SelectContent>
                  {(accounts || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="inline-flex items-center gap-2">
                        <span>{a.name}</span>
                        {a.last4 ? (
                          <span className="text-[11px] tabular-nums text-muted-foreground tracking-widest">
                            ••{a.last4}
                          </span>
                        ) : null}
                        {a.currency ? (
                          <span className="text-[11px] text-muted-foreground">
                            · {a.currency}
                          </span>
                        ) : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="ghost"
                onClick={() => setMode('choose')}
                className="rounded-xl"
                data-testid="statement-account-back"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleLink}
                disabled={!pickedId}
                className="rounded-xl"
                data-testid="statement-account-confirm-link"
              >
                Use this account
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
